import { HeaderCTAButton } from '@/components/buttons/HeaderCTAButton';
import { XCloseIcon } from '@/components/icons';
import { AppLink } from '@/components/links/AppLink';
import { useManifest } from '@/contexts/ManifestContext';
import LanguageSelector from '@/layout/LanguageSelector';
import { Routes } from '@/routes';
import { cn } from '@/utils/cn';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SlideMenuProps {
  isOpen: boolean;
  toggle: (value: boolean) => void;
}

// Menu links use the body font (DM Sans), medium, 20px, green-black — matching
// the design (not the Oswald display font used for headings/CTAs).
const menuLinkClassName =
  'text-primary-alt font-body block w-full text-xl font-medium leading-normal whitespace-nowrap hover:opacity-80';

const SlideMenu = ({ isOpen, toggle }: SlideMenuProps) => {
  const { t } = useTranslation();
  const { features, slideMenu, type, mainSiteUrl } = useManifest();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navItems = [
    {
      label: t('layout.menu.projects'),
      route: Routes.Projects,
      external: false,
      show: true,
    },
    {
      // The /impact route is labelled "Updates" in the navigation.
      label: t('layout.menu.impact'),
      route: Routes.Impact,
      external: false,
      show: features.showUpdatesNav !== false,
    },
    {
      label: t('layout.menu.supporters'),
      route: Routes.Supporters,
      external: false,
      show: features.showSupportersNav !== false,
    },
    {
      label: t('layout.menu.about'),
      route: 'https://resources.sumthing.org/about',
      external: true,
      show: features.showAboutNav,
    },
    {
      label: t('layout.menu.for-business'),
      route: Routes.Business,
      external: false,
      show: features.showBusinessNav,
    },
  ];

  return (
    <div
      className={cn(
        'bg-neutral-white text-primary-alt overflow-y-structure fixed inset-0 z-100 flex transform flex-col px-5 pt-3 pb-10 transition-transform duration-300 ease-in-out',
        'slide-menu-mobile',
        isOpen ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <div className="flex items-center justify-between">
        {/* When clicking the Logo, we do a full refresh of the React App and not just navigate home */}
        <AppLink
          href={mainSiteUrl ?? Routes.Home}
          className="group inline-block"
          onClick={() => toggle(false)}
        >
          <img
            src={slideMenu.logo.url}
            className="h-10 w-auto group-hover:opacity-90"
          />
        </AppLink>
        <button
          onClick={() => toggle(false)}
          aria-label={t('layout.menu.close')}
          className={cn(
            'text-primary-alt flex shrink-0 items-center justify-center transition-opacity hover:cursor-pointer hover:opacity-80',
            // Match the hamburger: circular outlined button for Sumthing, bare
            // icon for white-labels (partner).
            type !== 'partner' &&
              'border-primary-alt h-12.5 w-12.5 rounded-full border-2',
          )}
        >
          <XCloseIcon className="h-6 w-6" />
        </button>
      </div>

      <nav className="mt-8">
        <div className="flex flex-col gap-6">
          {navItems
            .filter((item) => item.show)
            .map(({ label, route, external }) =>
              external ? (
                <a
                  key={route}
                  className={menuLinkClassName}
                  href={route}
                  aria-label={label}
                >
                  {label}
                </a>
              ) : (
                <AppLink
                  key={route}
                  onClick={() => toggle(false)}
                  className={menuLinkClassName}
                  href={route}
                  aria-label={label}
                >
                  {label}
                </AppLink>
              ),
            )}
        </div>

        {features.enableI18n && (
          <div className="border-neutral-light mt-6 border-t pt-6">
            <LanguageSelector variant="inline" onSelect={() => toggle(false)} />
          </div>
        )}
      </nav>

      {/* Primary CTA pinned to the bottom, within thumb reach. */}
      <div className="mt-auto pt-8">
        <HeaderCTAButton
          size="lg"
          className="w-full"
          onClick={() => toggle(false)}
        />
      </div>
    </div>
  );
};

export default SlideMenu;
